// Point of Sale (/pos) — the counter app for selling drinks, snacks and gear
// alongside court bookings.
//
// Its constants live HERE rather than in bookingConst.js on purpose. The two
// share a Firebase project and a palette and nothing else: no POS value is
// read by the booking app, and no booking value is read here. Folding them
// together would grow an already 26KB file with a second product's
// vocabulary and make it harder to see what the booking app actually
// depends on.

// ---- Firebase nodes ----
//
// MULTI-TENANT LAYOUT. Every client's trading data lives in its own subtree
// under posTenants/<clientId>/, so one client is one branch of the tree: it
// can be secured with a single $clientId rule, read without touching anyone
// else's data, and removed in one delete. The four names below are LEAF names
// within that subtree, not root nodes — posUtil's tenantRef() is the only
// place that joins them up.
export const POS_TENANTS_NODE = 'posTenants';
export const POS_CATEGORIES_NODE = 'categories';
/**
 * Category icons, keyed BY CATEGORY NAME, deliberately apart from the
 * category list itself.
 *
 * `categories` stays the plain array of strings every product and order
 * already references, so adding icons needed no migration and a client that
 * never sets one has no node here at all. A missing key is simply "no icon".
 */
export const POS_CATEGORY_ICONS_NODE = 'categoryIcons';
export const POS_PRODUCTS_NODE = 'products';
export const POS_ORDERS_NODE = 'orders';
export const POS_AUDIT_NODE = 'auditLog';
// This client's own settings — today just its theme. Under posTenants rather
// than posClients because posClients is admin-only to write, and a club
// changing its own colours should not need an administrator.
export const POS_CONFIG_NODE = 'config';

// The client registry itself, and the settings that apply to every client.
// Both sit OUTSIDE posTenants: they describe the estate rather than belonging
// to any one member of it, and only an admin may write them.
export const POS_CLIENTS_NODE = 'posClients';
export const POS_GLOBAL_CONFIG_NODE = 'posGlobalConfig';

// ---- Admin ----

// The only accounts that may open /admin. Checked in the app AND in
// database.rules.json against auth.token.email — the UI check alone would be
// a suggestion, since anyone can call the database directly.
export const POS_ADMIN_EMAILS = ['admin@gmail.com', 'system@gmail.com'];

// The accounts that may open ANY client's till without being on its staff
// list. A STRICT SUBSET of the administrators above, and deliberately so:
// running the estate is not the same permission as standing at a particular
// club's counter, and an admin account that could ring up sales anywhere
// would make every client's audit trail ambiguous about who was there.
//
// Everyone else — admin@gmail.com included — signs in at a till only if that
// client's staff list names them.
export const POS_SUPERUSER_EMAILS = ['system@gmail.com'];

// Screens in the admin menu, in order. The description is the second line in
// the drawer — a menu of bare nouns makes someone open three screens to find
// the one they wanted.
export const POS_ADMIN_TABS = [
  { key: 'clients', label: 'Clients', description: 'Every registered client' },
  { key: 'registration', label: 'Client Registration', description: 'Add or edit a client' },
  { key: 'plans', label: 'Subscription Plans', description: 'Plans and free trials clients can avail' },
  { key: 'signups', label: 'Sign-up Requests', description: 'Subscriptions and demo calls from the website' },
  { key: 'config', label: 'Config', description: 'Settings for every client' },
];

/**
 * The sections of the admin Config screen. General is the settings every till
 * reads; Admin Settings is what the Website Pal itself shows clients when they
 * pay for a subscription (our QR code and account details).
 */
export const POS_ADMIN_CONFIG_TABS = [
  { key: 'general', label: 'General' },
  { key: 'adminSettings', label: 'Admin Settings' },
];

// ---- Subscriptions ----
//
// SHAPE.
//
//   posSubscriptionPlans/<planId>                      what can be bought
//   posAdminSettings                                   our QR code, account name,
//                                                      extra-staff price
//   posClients/<clientId>/subscription                 what the client HAS
//   posSubscriptionApplications/<clientId>/<appId>     what a client ASKED for
//   posSubscriptionReceipts/<clientId>/<appId>         the proof of payment
//
// The receipt is split from the application on purpose: an application is a
// few hundred bytes and is listed on every admin visit, a receipt photo is up
// to ~250KB and is only ever needed when someone opens that one application.

export const POS_SUBSCRIPTION_PLANS_NODE = 'posSubscriptionPlans';
export const POS_ADMIN_SETTINGS_NODE = 'posAdminSettings';
export const POS_CLIENT_SUBSCRIPTION_NODE = 'subscription';
export const POS_SUBSCRIPTION_APPLICATIONS_NODE = 'posSubscriptionApplications';
export const POS_SUBSCRIPTION_RECEIPTS_NODE = 'posSubscriptionReceipts';
// The admin's own trail. A plan or our QR code is not any one client's
// business, so it cannot go on a client's trail.
export const POS_ADMIN_AUDIT_NODE = 'posAdminAuditLog';

/**
 * Whether a client's till is open at all, under posClients/<clientId>/.
 * Absent means open. Set only by an administrator, usually after a plan or a
 * free trial has expired and the club has not renewed.
 */
export const POS_CLIENT_ACCESS_NODE = 'access';

/** Why a client was disabled — recorded, and shown on the admin card. */
export const POS_CLIENT_DISABLED_REASON = 'subscription_expired';

/** How a plan's length is counted. */
export const POS_PLAN_DURATION_UNITS = [
  { key: 'days', label: 'Day(s)' },
  { key: 'months', label: 'Month(s)' },
];
export const POS_PLAN_DURATION_UNIT_KEYS = POS_PLAN_DURATION_UNITS.map((unit) => unit.key);
export const POS_PLAN_DURATION_MAX = 1000;
export const POS_PLAN_NAME_MAX_LENGTH = 40;
export const POS_PLAN_DESCRIPTION_MAX_LENGTH = 200;
export const POS_PLAN_FEATURE_MAX_LENGTH = 80;
export const POS_PLAN_FEATURES_MAX = 10;
export const POS_PLAN_PRICE_MAX = 1000000;
export const POS_PLAN_BUTTON_LABEL_MAX_LENGTH = 40;
export const POS_PLAN_FINE_PRINT_MAX_LENGTH = 200;
export const POS_PLAN_STAFF_NOTE_MAX_LENGTH = 60;
/** The most staff accounts a plan can include for free. */
export const POS_PLAN_FREE_STAFF_MAX = 50;

/**
 * Price of ONE additional staff account, for the whole length of the plan it
 * is bought with. The default — Admin Settings can change it.
 */
export const POS_EXTRA_STAFF_PRICE_DEFAULT = 49;

/** The most extra staff accounts one application may ask for. */
export const POS_EXTRA_STAFF_MAX = 20;

/**
 * Price of the Warehouse / Stock Room add-on offered on the landing page's
 * pricing card. The default — Admin Settings can change it.
 */
export const POS_WAREHOUSE_PRICE_DEFAULT = 49;

/** A business has one warehouse at most, so the add-on stepper stops at 1. */
export const POS_WAREHOUSE_ADDON_MAX = 1;

// ---- Public sign-ups (the landing page at /) ----
//
// SHAPE.
//
//   posSignupRequests/<requestId>     a visitor's subscription or demo-call request
//   posSignupReceipts/<requestId>     the proof of payment for a subscription request
//
// Separate from posSubscriptionApplications on purpose: an application
// belongs to a client that already exists, and a visitor on the landing page
// is not a client yet. The receipt is split from the request for the same
// reason as there — the admin list never downloads the photos.
export const POS_SIGNUP_REQUESTS_NODE = 'posSignupRequests';
export const POS_SIGNUP_RECEIPTS_NODE = 'posSignupReceipts';

/** What a visitor asked for. */
export const POS_SIGNUP_TYPES = {
  SUBSCRIBE: 'subscribe',
  DEMO: 'demo',
  TRIAL: 'trial',
};

/** Where one sign-up request stands. */
export const POS_SIGNUP_STATUS = {
  PENDING: 'pending',
  DONE: 'done',
  DISMISSED: 'dismissed',
};

/** How a sign-up request's status reads on the admin screen. */
export const POS_SIGNUP_STATUS_LABELS = {
  pending: 'New',
  done: 'Done',
  dismissed: 'Dismissed',
};

/** How a sign-up request's type reads on the admin screen. */
export const POS_SIGNUP_TYPE_LABELS = {
  subscribe: 'Subscription',
  demo: 'Demo call',
  trial: 'Free trial',
};

/** Longest value accepted per sign-up field — mirrored in database.rules.json. */
export const POS_SIGNUP_FIELD_MAX = {
  name: 80,
  business: 120,
  email: 254,
  phone: 30,
  about: 1000,
  features: 1000,
  note: 300,
};

/** How long a visitor has to pay once the QR code is shown. */
export const POS_SIGNUP_PAY_WINDOW_SECONDS = 600;

/**
 * Demo calls: 30-minute slots from 6:00 PM, Philippine time, eighteen of them
 * — so the last one starts at 2:30 AM the next day and ends at 3:00 AM.
 */
export const POS_DEMO_SLOT_START_MINUTES = 18 * 60;
export const POS_DEMO_SLOT_LENGTH_MINUTES = 30;
export const POS_DEMO_SLOT_COUNT = 18;

/**
 * Days before the end date at which the till starts warning. A week gives a
 * club one full weekly cycle to pay and still have 1-2 days for review.
 */
export const POS_SUBSCRIPTION_WARN_DAYS = 7;

/**
 * Fewer days left than this and the warning turns RED rather than amber — at
 * 2 days a club paying now may still be waiting on the 1–2 day review when
 * the plan runs out.
 */
export const POS_SUBSCRIPTION_URGENT_DAYS = 3;

/**
 * How long an EXPIRED till keeps working normally before the "expired" popup
 * appears over whatever screen is open — so the club sees the message over
 * the work they were doing, not instead of the page they opened.
 */
export const POS_EXPIRED_GRACE_MS = 5000;

/**
 * How often the expired lock checks it is still on screen, and puts itself
 * back if it was removed or hidden (developer tools, a browser extension).
 */
export const POS_EXPIRED_WATCHDOG_MS = 1000;

/** How the Sales screen scopes its report for staff who are not store admins. */
export const POS_SALES_SCOPES = [
  { key: 'mine', label: 'My sales' },
  { key: 'all', label: 'Overall' },
];
export const POS_SALES_SCOPE_DEFAULT = 'mine';

// ---- Deleted (cancelled) sales ----
// A deleted sale is NOT removed from the database. The order stays exactly as
// it was taken and gains a `deletion` record beside it (who, when, why), so
// the money trail can always be reconstructed and the reason read back.

/** The child of an order that marks it deleted. Written once, never edited. */
export const POS_ORDER_DELETION_KEY = 'deletion';

/** Which sales the Sales preview lists. */
export const POS_SALE_STATUS_COMPLETED = 'completed';
export const POS_SALE_STATUS_DELETED = 'deleted';
export const POS_SALE_STATUS_ALL = 'all';
export const POS_SALE_STATUS_FILTERS = [
  { key: POS_SALE_STATUS_COMPLETED, label: 'Completed' },
  { key: POS_SALE_STATUS_DELETED, label: 'Cancelled/Deleted' },
  { key: POS_SALE_STATUS_ALL, label: 'All' },
];
export const POS_SALE_STATUS_DEFAULT = POS_SALE_STATUS_COMPLETED;

/**
 * The reason box on the delete-sale prompt. 300 matches the .validate on
 * orders/$orderId/deletion/reason in database.rules.json — raising one means
 * raising the other.
 */
export const POS_DELETE_REASON_MIN_LENGTH = 3;
export const POS_DELETE_REASON_MAX_LENGTH = 300;

/** One-tap reasons on the delete-sale prompt; they fill the box, which stays editable. */
export const POS_DELETE_REASON_PRESETS = [
  'Customer cancelled',
  'Wrong item entered',
  'Duplicate sale',
  'Test sale',
];

/**
 * Every client's dates are counted in ITS OWN timezone. A plan that "ends on
 * the 30th" ends at midnight on the 30th where the club is, not where the
 * server or the administrator happens to be.
 */
export const POS_DEFAULT_TIMEZONE = 'Asia/Manila';
// `short` is what is printed beside a date ("Sep 26, 2026 (PH Time)"), so a
// club always knows which clock "ends on the 26th" is measured by.
export const POS_TIMEZONES = [
  { key: 'Asia/Manila', short: 'PH Time', label: 'Philippines (Asia/Manila, UTC+8)' },
  { key: 'Asia/Singapore', short: 'SG Time', label: 'Singapore (UTC+8)' },
  { key: 'Asia/Hong_Kong', short: 'HK Time', label: 'Hong Kong (UTC+8)' },
  { key: 'Asia/Taipei', short: 'TW Time', label: 'Taiwan (UTC+8)' },
  { key: 'Asia/Kuala_Lumpur', short: 'MY Time', label: 'Malaysia (UTC+8)' },
  { key: 'Asia/Jakarta', short: 'WIB', label: 'Indonesia — Jakarta (UTC+7)' },
  { key: 'Asia/Bangkok', short: 'TH Time', label: 'Thailand (UTC+7)' },
  { key: 'Asia/Ho_Chi_Minh', short: 'VN Time', label: 'Vietnam (UTC+7)' },
  { key: 'Asia/Tokyo', short: 'JP Time', label: 'Japan (UTC+9)' },
  { key: 'Asia/Seoul', short: 'KR Time', label: 'South Korea (UTC+9)' },
  { key: 'Asia/Kolkata', short: 'IST', label: 'India (UTC+5:30)' },
  { key: 'Asia/Dubai', short: 'UAE Time', label: 'UAE (UTC+4)' },
  { key: 'Australia/Sydney', short: 'Sydney Time', label: 'Australia — Sydney' },
  { key: 'Europe/London', short: 'UK Time', label: 'United Kingdom' },
  { key: 'America/New_York', short: 'US Eastern Time', label: 'US — Eastern' },
  { key: 'America/Los_Angeles', short: 'US Pacific Time', label: 'US — Pacific' },
  { key: 'UTC', short: 'UTC', label: 'UTC' },
];

/** Where a client's subscription stands today. */
export const POS_SUBSCRIPTION_STATUS = {
  NONE: 'none',
  SCHEDULED: 'scheduled',
  ACTIVE: 'active',
  EXPIRING: 'expiring',
  EXPIRED: 'expired',
};

/** Where one application stands. */
export const POS_APPLICATION_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

export const POS_APPLICATION_REJECT_REASON_MAX_LENGTH = 300;

/** Who reviews applications, as the client reads it. */
export const POS_SUBSCRIPTION_VENDOR_NAME = 'Kawnter by The Website Pal';

/** How long a review takes, quoted on the confirmation screen. */
export const POS_SUBSCRIPTION_REVIEW_WINDOW = '1–2 business days';

/** How many past applications the till's Subscriptions screen lists. */
export const POS_APPLICATION_HISTORY_LIMIT = 5;

/** How an application's status reads to the client. */
export const POS_APPLICATION_STATUS_LABELS = {
  pending: 'Under review',
  approved: 'Approved',
  rejected: 'Not approved',
};

/** Actions on the admin's own trail (POS_ADMIN_AUDIT_NODE). */
export const POS_ADMIN_AUDIT_ACTIONS = {
  PLAN_CREATED: 'pos_plan_created',
  PLAN_UPDATED: 'pos_plan_updated',
  PLAN_DELETED: 'pos_plan_deleted',
  ADMIN_SETTINGS_UPDATED: 'pos_admin_settings_updated',
  APPLICATION_APPROVED: 'pos_subscription_approved',
  APPLICATION_REJECTED: 'pos_subscription_rejected',
  CLIENT_DISABLED: 'pos_client_disabled',
  CLIENT_ENABLED: 'pos_client_enabled',
  SIGNUP_COMPLETED: 'pos_signup_completed',
  SIGNUP_DISMISSED: 'pos_signup_dismissed',
  SIGNUP_REOPENED: 'pos_signup_reopened',
  ADMIN_SIGNED_IN: 'pos_admin_signed_in',
  ADMIN_SIGNED_OUT: 'pos_admin_signed_out',
  // An email to a client or sign-up visitor that an admin's action triggered
  // (utils/emailServiceUtil.js) — sent, or attempted and failed.
  CLIENT_EMAIL_SENT: 'pos_client_email_sent',
  CLIENT_EMAIL_FAILED: 'pos_client_email_failed',
};

/**
 * Client ids that can never be registered, because a client of that id would
 * be unreachable: /admin is the admin screen, so a client called "admin"
 * would have no URL of its own. Compared lower-case.
 */
export const POS_RESERVED_CLIENT_IDS = ['admin', 'pos', 'new', 'null', 'undefined', 'checkout'];

/**
 * What a client id may look like. It becomes a URL segment AND a Firebase key,
 * so it is restricted to the intersection of what both accept comfortably:
 * lower-case letters, digits and single hyphens. Firebase additionally forbids
 * . # $ [ ] / in keys, all of which this excludes anyway.
 */
/**
 * The top-level domain every client's staff addresses sit under:
 * <anything>@<client-id>.<this>. Partitioning the namespace by client id is
 * what stops two clients claiming the same address — see clientEmailDomain in
 * utils/posClientUtil.js.
 */
export const POS_STAFF_EMAIL_TLD = 'com';

export const POS_CLIENT_ID_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const POS_CLIENT_ID_MAX_LENGTH = 40;

// ---- Audit ----
// A SEPARATE trail from the booking app's /auditLog. A counter sale and a
// court reservation are different businesses to review, and mixing them would
// bury one in the other — the booking trail is already the busier node.
export const POS_AUDIT_ACTIONS = {
  PRODUCT_CREATED: 'pos_product_created',
  PRODUCT_UPDATED: 'pos_product_updated',
  PRODUCT_DELETED: 'pos_product_deleted',
  STOCK_ADDED: 'pos_stock_added',
  STOCK_ADJUSTED: 'pos_stock_adjusted',
  THEME_UPDATED: 'pos_theme_updated',
  SETTINGS_UPDATED: 'pos_settings_updated',
  PAYMENTS_UPDATED: 'pos_payments_updated',
  BRANDING_UPDATED: 'pos_branding_updated',
  RESERVE_ADDED: 'pos_reserve_added',
  RESERVE_ADJUSTED: 'pos_reserve_adjusted',
  STOCK_REPLENISHED: 'pos_stock_replenished',
  VAT_UPDATED: 'pos_vat_updated',
  CATEGORY_ADDED: 'pos_category_added',
  CATEGORY_REMOVED: 'pos_category_removed',
  CATEGORY_ICON_SET: 'pos_category_icon_set',
  ORDER_PLACED: 'pos_order_placed',
  ORDER_DELETED: 'pos_order_deleted',
  SUBSCRIPTION_SET: 'pos_subscription_set',
  SUBSCRIPTION_APPLIED: 'pos_subscription_applied',
  SUBSCRIPTION_APPROVED: 'pos_subscription_approved',
  SUBSCRIPTION_REJECTED: 'pos_subscription_rejected',
  CLIENT_DISABLED: 'pos_client_disabled',
  CLIENT_ENABLED: 'pos_client_enabled',
  ROLE_RIGHTS_UPDATED: 'pos_role_rights_updated',
  ROLE_RIGHTS_RESET: 'pos_role_rights_reset',
  CUSTOM_SORT_SAVED: 'pos_custom_sort_saved',
  SORT_PREFERENCE_CHANGED: 'pos_sort_preference_changed',
  FONT_UPLOADED: 'pos_font_uploaded',
  FONT_REMOVED: 'pos_font_removed',
  FEATURES_UPDATED: 'pos_features_updated',
  STAFF_SIGNED_IN: 'pos_staff_signed_in',
  STAFF_SIGNED_OUT: 'pos_staff_signed_out',
};

export const POS_AUDIT_LABELS = {
  [POS_AUDIT_ACTIONS.PRODUCT_CREATED]: 'Product registered',
  [POS_AUDIT_ACTIONS.PRODUCT_UPDATED]: 'Product updated',
  [POS_AUDIT_ACTIONS.PRODUCT_DELETED]: 'Product removed',
  [POS_AUDIT_ACTIONS.STOCK_ADDED]: 'Stock added',
  [POS_AUDIT_ACTIONS.STOCK_ADJUSTED]: 'Stock corrected',
  [POS_AUDIT_ACTIONS.THEME_UPDATED]: 'Theme updated',
  [POS_AUDIT_ACTIONS.SETTINGS_UPDATED]: 'Product settings updated',
  [POS_AUDIT_ACTIONS.PAYMENTS_UPDATED]: 'Payment methods updated',
  [POS_AUDIT_ACTIONS.BRANDING_UPDATED]: 'Store details updated',
  [POS_AUDIT_ACTIONS.RESERVE_ADDED]: 'Reserve stock added',
  [POS_AUDIT_ACTIONS.RESERVE_ADJUSTED]: 'Reserve stock corrected',
  [POS_AUDIT_ACTIONS.STOCK_REPLENISHED]: 'Active stock replenished',
  [POS_AUDIT_ACTIONS.VAT_UPDATED]: 'VAT settings updated',
  [POS_AUDIT_ACTIONS.CATEGORY_ADDED]: 'Category added',
  [POS_AUDIT_ACTIONS.CATEGORY_REMOVED]: 'Category removed',
  [POS_AUDIT_ACTIONS.CATEGORY_ICON_SET]: 'Category icon changed',
  [POS_AUDIT_ACTIONS.ORDER_PLACED]: 'Order placed',
  [POS_AUDIT_ACTIONS.ORDER_DELETED]: 'Sale deleted',
  [POS_AUDIT_ACTIONS.SUBSCRIPTION_SET]: 'Subscription set by administrator',
  [POS_AUDIT_ACTIONS.SUBSCRIPTION_APPLIED]: 'Subscription application submitted',
  [POS_AUDIT_ACTIONS.SUBSCRIPTION_APPROVED]: 'Subscription application approved',
  [POS_AUDIT_ACTIONS.SUBSCRIPTION_REJECTED]: 'Subscription application rejected',
  [POS_AUDIT_ACTIONS.CLIENT_DISABLED]: 'Till disabled by administrator',
  [POS_AUDIT_ACTIONS.CLIENT_ENABLED]: 'Till re-enabled',
  [POS_AUDIT_ACTIONS.ROLE_RIGHTS_UPDATED]: 'Staff role rights changed',
  [POS_AUDIT_ACTIONS.ROLE_RIGHTS_RESET]: 'Staff role rights reset to default',
  [POS_AUDIT_ACTIONS.CUSTOM_SORT_SAVED]: 'Custom product order saved',
  [POS_AUDIT_ACTIONS.SORT_PREFERENCE_CHANGED]: 'Product sort changed',
  [POS_AUDIT_ACTIONS.FONT_UPLOADED]: 'Font uploaded',
  [POS_AUDIT_ACTIONS.FONT_REMOVED]: 'Font removed',
  [POS_AUDIT_ACTIONS.FEATURES_UPDATED]: 'Feature settings changed by administrator',
  [POS_AUDIT_ACTIONS.STAFF_SIGNED_IN]: 'Signed in',
  [POS_AUDIT_ACTIONS.STAFF_SIGNED_OUT]: 'Signed out',
};

// How loudly each action reads on the trail. Selling and restocking are
// routine; removing a product or a category destroys something.
export const POS_AUDIT_TONES = {
  [POS_AUDIT_ACTIONS.PRODUCT_CREATED]: 'info',
  [POS_AUDIT_ACTIONS.PRODUCT_UPDATED]: 'warning',
  [POS_AUDIT_ACTIONS.PRODUCT_DELETED]: 'danger',
  // Routine, and the good kind of routine: something arrived.
  [POS_AUDIT_ACTIONS.STOCK_ADDED]: 'success',
  // Not routine: someone overrode the count the till had been keeping, so the
  // difference between the two is worth being able to find later.
  [POS_AUDIT_ACTIONS.STOCK_ADJUSTED]: 'warning',
  // Changes what everyone sees, so it is worth noticing on the trail.
  [POS_AUDIT_ACTIONS.THEME_UPDATED]: 'warning',
  // Changes what every screen in this till lists, so it is worth noticing.
  [POS_AUDIT_ACTIONS.SETTINGS_UPDATED]: 'warning',
  // Changes how money can be taken at the counter.
  [POS_AUDIT_ACTIONS.PAYMENTS_UPDATED]: 'warning',
  // Cosmetic, and visible to everyone the moment it saves.
  [POS_AUDIT_ACTIONS.BRANDING_UPDATED]: 'info',
  // A delivery into the storeroom: routine, and the good kind.
  [POS_AUDIT_ACTIONS.RESERVE_ADDED]: 'success',
  // Someone overrode the count the till was keeping, so the difference is
  // worth being able to find later.
  [POS_AUDIT_ACTIONS.RESERVE_ADJUSTED]: 'warning',
  // Nothing was created or destroyed — stock moved from the storeroom to the
  // shelf — so this is information rather than a warning.
  [POS_AUDIT_ACTIONS.STOCK_REPLENISHED]: 'info',
  // Changes what every customer is charged from the next sale onward, and only
  // an administrator can have done it — worth standing out on the club's own
  // trail, where the staff who noticed the totals move will go looking.
  [POS_AUDIT_ACTIONS.VAT_UPDATED]: 'warning',
  [POS_AUDIT_ACTIONS.CATEGORY_ADDED]: 'info',
  [POS_AUDIT_ACTIONS.CATEGORY_REMOVED]: 'danger',
  [POS_AUDIT_ACTIONS.CATEGORY_ICON_SET]: 'info',
  [POS_AUDIT_ACTIONS.ORDER_PLACED]: 'success',
  // Takes money out of the books — the one sale action that must stand out.
  [POS_AUDIT_ACTIONS.ORDER_DELETED]: 'danger',
  // Changes how long this till is paid up to, and only an administrator can.
  [POS_AUDIT_ACTIONS.SUBSCRIPTION_SET]: 'warning',
  [POS_AUDIT_ACTIONS.SUBSCRIPTION_APPLIED]: 'info',
  [POS_AUDIT_ACTIONS.SUBSCRIPTION_APPROVED]: 'success',
  [POS_AUDIT_ACTIONS.SUBSCRIPTION_REJECTED]: 'danger',
  [POS_AUDIT_ACTIONS.CLIENT_DISABLED]: 'danger',
  [POS_AUDIT_ACTIONS.CLIENT_ENABLED]: 'success',
  // Changes what someone at this counter can see and do.
  [POS_AUDIT_ACTIONS.ROLE_RIGHTS_UPDATED]: 'warning',
  [POS_AUDIT_ACTIONS.ROLE_RIGHTS_RESET]: 'info',
  // Changes the order every till lists its products in.
  [POS_AUDIT_ACTIONS.CUSTOM_SORT_SAVED]: 'info',
  // Only which order the grid opens in next time; no data changes.
  [POS_AUDIT_ACTIONS.SORT_PREFERENCE_CHANGED]: 'info',
  [POS_AUDIT_ACTIONS.FONT_UPLOADED]: 'info',
  [POS_AUDIT_ACTIONS.FONT_REMOVED]: 'warning',
  // Switches whole screens and stock behaviour on or off for this club, and
  // only an administrator can — worth standing out on the club's own trail.
  [POS_AUDIT_ACTIONS.FEATURES_UPDATED]: 'warning',
  // Routine — but the sign-in screen promises every sign-in is on the trail,
  // and "who was at the till at 3pm" is a question the trail should answer.
  [POS_AUDIT_ACTIONS.STAFF_SIGNED_IN]: 'info',
  [POS_AUDIT_ACTIONS.STAFF_SIGNED_OUT]: 'info',
};

// ---- Roles & permissions ----
//
// A role is a named set of permissions, defined PER CLIENT: one club's
// "Supervisor" has nothing to do with another's, and a club that wants a
// single role for everyone should not have to reason about anyone else's.
//
// KEYS ARE STORED AS FIREBASE KEYS, so they carry no dots — RTDB forbids
// . # $ [ ] / in a key, and `inventory.view` would be rejected. They are also
// permanent: a saved role holds these strings, so renaming one silently
// revokes whatever it granted. The label beside it is free to change.
export const POS_PERMISSIONS = [
  { key: 'viewProducts', label: 'Can view Products' },
  { key: 'processOrders', label: 'Can process orders' },
  { key: 'viewInventory', label: 'Can visit inventory screen' },
  { key: 'addStock', label: 'Can add stocks' },
  { key: 'viewSales', label: 'Can view Sales Screen' },
  { key: 'exportSales', label: 'Can export sales' },
  { key: 'deleteSales', label: 'Can delete sales' },
  { key: 'viewAuditTrail', label: 'Can view audit trail screen' },
  { key: 'manageConfig', label: 'Can change settings and manage the subscription' },
];

/** Every permission key, for validating what is read back from the database. */
export const POS_PERMISSION_KEYS = POS_PERMISSIONS.map((permission) => permission.key);

/** Where a client's roles live, under posClients/<clientId>/. */
export const POS_ROLES_NODE = 'roles';

/**
 * The club's OWN changes to its roles' rights, under posTenants/<clientId>/.
 * Keyed by role id. A role with an entry here uses these permissions instead
 * of the defaults the administrator set; no entry means the default applies.
 * Kept in the club's subtree (which the club can write) rather than next to
 * the roles (which only an administrator can), so the default is never lost.
 */
export const POS_ROLE_OVERRIDES_NODE = 'roleOverrides';

/**
 * Role names treated as the club's store admin even without the "Store admin"
 * box ticked. Compared trimmed and lower-case. A store-admin role has full
 * access and is read-only on the club's Staff Access screen.
 */
export const POS_STORE_ADMIN_ROLE_NAMES = ['admin'];

/**
 * A role name is a label people pick from, not an identifier — it just has to
 * fit on screen and be typed by a person.
 */
export const POS_ROLE_NAME_MAX_LENGTH = 40;

/**
 * What a staff account with NO role assigned may do: everything.
 *
 * Deliberately permissive, and the reason is migration. Every staff account
 * that existed before roles did has no role, so flipping this to false would
 * shut every one of them out of every screen the moment it is deployed — at a
 * counter, mid-shift, with no way for them to fix it. Roles are therefore
 * opt-in PER ACCOUNT: assign one and it is enforced from that moment.
 *
 * Set this to false once every account at every client has a role. An
 * unassigned account then becomes one that can do nothing, which is the right
 * end state and the wrong starting one.
 */
export const POS_UNASSIGNED_STAFF_UNRESTRICTED = true;

// ---- Screens ----
export const POS_TABS = [
  { key: 'dashboard', label: 'Products', description: 'Sell and take payment' },
  { key: 'sales', label: 'Sales', description: 'Takings and reports' },
  { key: 'inventory', label: 'Inventory', description: 'Stock, products and categories' },
  { key: 'audit', label: 'Audit Trail', description: 'Track till activity' },
  { key: 'subscriptions', label: 'Subscriptions', description: 'Your plan and other plans' },
  { key: 'config', label: 'Config', description: 'App settings' },
];

/**
 * The permission each screen above needs before it is offered at all.
 *
 * Keyed by the tab keys in POS_TABS. A tab missing from this map is ungated —
 * there is no such tab today, and a new screen has to be listed here
 * deliberately rather than inheriting a permission by accident.
 */
export const POS_TAB_PERMISSIONS = {
  dashboard: 'viewProducts',
  sales: 'viewSales',
  inventory: 'viewInventory',
  audit: 'viewAuditTrail',
  // The same permission as the Subscribe button: whoever may pay for the
  // till is whoever may see what it is paying for. Reused rather than a new
  // permission, so no existing role has to be edited to keep its access.
  subscriptions: 'manageConfig',
  config: 'manageConfig',
};

/**
 * Rows per page in every paged POS list — the sales preview and the audit
 * trail. Named here rather than per-screen so the two cannot disagree about
 * what a page is; PosPager reads it as its default.
 */
export const POS_PAGE_SIZE = 10;

/** The one thing staff must not get wrong, so it is stated on the receipt. */
export const POS_PAYMENT_NOTE = 'Only tap Confirm once the customer has paid.';

// ---- Mode of payment ----
// How the customer paid, chosen on the confirm step and recorded per order.
// The till used to infer "not cash" from an empty cash field, which could not
// tell GCash from Maya from a bank transfer — so a client reconciling a day's
// takings had one drawer figure and one undifferentiated remainder.
//
// Emoji rather than brand logos: the GCash and Maya marks belong to their
// owners and would have to be licensed and shipped as image assets. Swap
// `emoji` for an <img> here if a client is entitled to use them.
export const POS_PAYMENT_METHODS = [
  { key: 'cash', label: 'Cash', emoji: '💵' },
  { key: 'gcash', label: 'GCash', emoji: '📱' },
  { key: 'maya', label: 'Maya', emoji: '💳' },
  { key: 'bank', label: 'Bank Transfer', emoji: '🏦' },
];

/** The method a fresh confirm step opens on — most counter sales are cash. */
export const POS_DEFAULT_PAYMENT_METHOD = 'cash';

/**
 * The only method that tenders an amount and gives change back. The cash
 * field and the receipt's CASH/CHANGE rows both hang off this rather than
 * each testing for the string themselves.
 */
export const POS_CASH_METHOD = 'cash';

/** The Mode of Payment filter's "everything" choice on the Sales screen. */
export const POS_PAYMENT_FILTER_ALL = 'all';

/** The largest delivery fee the order confirmation accepts. */
export const POS_DELIVERY_FEE_MAX = 100000;

/** How many products the Sales best-seller export sheet ranks. */
export const POS_TOP_PRODUCTS_LIMIT = 10;

// ---- Sales trend ----
/**
 * How the Sales line chart buckets the period. Daily answers "which shift was
 * busy", yearly answers "are we growing" — the same orders, asked two
 * different questions, so the granularity is the reader's to choose.
 */
export const POS_SALES_GRANULARITIES = [
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'yearly', label: 'Yearly' },
];

/** The granularity picked for a multi-day range until staff choose another. */
export const POS_DEFAULT_SALES_GRANULARITY = 'daily';

/**
 * Hour-by-hour buckets. NOT one of the toggle choices above: it is used
 * automatically whenever the report covers a single day, where any of those
 * would plot just one point.
 */
export const POS_HOURLY_GRANULARITY = 'hourly';
export const POS_HOURLY_GRANULARITY_LABEL = 'Hourly';

/**
 * How many days the Sales screen opens on, counting today as one of them.
 *
 * Today only: the screen opens on the current day's takings, charted by hour
 * (see POS_HOURLY_GRANULARITY). Picking a range is for looking further back.
 */
export const POS_DEFAULT_RANGE_DAYS = 1;

/**
 * A hard stop on how many points the trend line will plot. Daily over a
 * decade-wide range would otherwise build tens of thousands of buckets and
 * draw a line nobody can read; past this the chart says so instead.
 */
export const POS_TREND_MAX_BUCKETS = 400;

/** Products shown in a category's mini bar chart, best first. */
export const POS_CATEGORY_BAR_LIMIT = 5;

/**
 * What the trend line measures. Amount answers "what did we take", quantity
 * answers "what moved" — a day of cheap snacks and a day of one expensive
 * order look identical on one axis and opposite on the other.
 */
export const POS_TREND_MEASURES = [
  { key: 'amount', label: 'Amount' },
  { key: 'quantity', label: 'Quantity' },
];
export const POS_DEFAULT_TREND_MEASURE = 'amount';

/** Whether the line is one total or one line per product category. */
export const POS_TREND_VIEWS = [
  { key: 'overall', label: 'Overall' },
  { key: 'category', label: 'Category' },
];
export const POS_DEFAULT_TREND_VIEW = 'overall';

/**
 * Line colours for the per-category view, assigned in FIXED ORDER and never
 * cycled — a ninth category folds into "Other" rather than reusing slot one,
 * because two lines sharing a colour is worse than one line being a bucket.
 *
 * Validated rather than picked by eye (dataviz validate_palette, light mode):
 * every slot clears the lightness band, the chroma floor and 3:1 against the
 * card, and the worst adjacent pair is ΔE 13.3 under deuteranopia — above the
 * 8 needed to stand alone without relying on the legend. The order matters as
 * much as the values; re-shuffling these can drop an adjacent pair below the
 * floor. Teal leads so the Overall line keeps the colour it has always had.
 */
export const POS_CATEGORY_COLORS = [
  '#0e9384', '#b54708', '#4f46e5', '#be123c',
  '#0369a1', '#3f6212', '#7c3aed', '#a16207',
];

/** Categories drawn as their own line; the rest are summed into "Other". */
export const POS_TREND_MAX_SERIES = POS_CATEGORY_COLORS.length;

/** The bucket label for everything past POS_TREND_MAX_SERIES. */
export const POS_TREND_OTHER_LABEL = 'Other';

/** Newest orders streamed into the Sales screen; older ones stay queryable. */
export const POS_RECENT_ORDERS_LIMIT = 300;

// Search runs from the first character typed (the brief), so this is 1 rather
// than the usual 2-3 — named here so the Inventory screen and any future
// caller cannot disagree about it.
export const POS_SEARCH_MIN_CHARS = 1;

/**
 * How few of something counts as running low, until an administrator says
 * otherwise (Admin -> Config).
 *
 * Five is a shift's worth of a fast-moving drink — enough warning to reorder,
 * not so much that every product wears a banner.
 */
export const POS_LOW_STOCK_DEFAULT = 5;

/**
 * The most it can be set to. A threshold above this would mark the entire
 * catalogue as low, which is the same as marking none of it.
 */
export const POS_LOW_STOCK_MAX = 999;

/** The "all categories" tab. Not a real category, so it cannot collide. */
export const POS_ALL_CATEGORIES = '__all__';

// ---- This client's own settings -------------------------------------------
//
// The tabs on the till's Config screen. Colours and behaviour are edited at
// different times by different people — a club sets its palette once and then
// leaves it, while a page size is tuned against the screen the counter
// actually runs on.
export const POS_CONFIG_TABS = [
  { key: 'theme', label: 'Theme', description: 'Colours used across this till' },
  { key: 'store', label: 'Store Details', description: 'Logo and typeface' },
  { key: 'payments', label: 'Payments', description: 'How this counter takes money' },
  { key: 'products', label: 'Product Settings', description: 'How products are listed' },
  // STORE ADMINS ONLY — PosConfigScreen hides it from everyone else.
  { key: 'staffAccess', label: 'Staff Access', description: 'What each staff role may do here' },
];

/** Config tabs only a store admin sees. */
export const POS_STORE_ADMIN_CONFIG_TABS = ['staffAccess'];

/** Just the keys, for validating a restored choice. */
export const POS_CONFIG_TAB_KEYS = POS_CONFIG_TABS.map((tab) => tab.key);

/**
 * The key this client's page size is stored under, in
 * posTenants/<clientId>/config beside the theme colours.
 *
 * NAMED HERE because database.rules.json has to carry a matching rule: every
 * other key in that node is validated as a hex colour, so a number would be
 * refused. See the "config" block in the rules.
 */
export const POS_PRODUCT_PAGE_SIZE_KEY = 'productPageSize';

/**
 * How many products a screen shows at once before it pages.
 *
 * ZERO MEANS SHOW EVERY PRODUCT, and that is the default — a club that never
 * opens this screen sees exactly what it saw before paging existed. Unset and
 * unusable values read as 0 too, so there is only one way for the setting to
 * be absent and it cannot be mistaken for "show nothing".
 */
export const POS_PRODUCT_PAGE_SIZE_DEFAULT = 0;

/** The fewest a page can hold once paging is on at all. */
export const POS_PRODUCT_PAGE_SIZE_MIN = 1;

/**
 * The most it can be set to. Past this the setting stops meaning anything —
 * a "page" of 200 products is the whole catalogue for every club running this
 * till, which is what 0 already says more clearly.
 */
export const POS_PRODUCT_PAGE_SIZE_MAX = 200;

/**
 * Product card size on the Products grid and Inventory's card view, in px,
 * under posTenants/<clientId>/config. 0 (or absent) means "automatic" — the
 * responsive column counts the grids have always used.
 *
 * Numbers, so database.rules.json carries named rules for both.
 */
export const POS_CARD_WIDTH_KEY = 'productCardWidth';
export const POS_CARD_HEIGHT_KEY = 'productCardHeight';
export const POS_CARD_WIDTH_MIN = 100;
export const POS_CARD_WIDTH_MAX = 480;
export const POS_CARD_HEIGHT_MIN = 120;
export const POS_CARD_HEIGHT_MAX = 600;

/**
 * Product card font sizes, in px, stored beside the card size. 0 (or absent)
 * keeps the stylesheet's own sizes, which also shrink on a phone.
 *
 *   name   - the product name
 *   price  - the price
 *   detail - description, stock count and the small tags on the card (the
 *            tags are drawn a little smaller than this so they stay tags)
 */
export const POS_CARD_NAME_FONT_KEY = 'productCardNameFont';
export const POS_CARD_PRICE_FONT_KEY = 'productCardPriceFont';
export const POS_CARD_DETAIL_FONT_KEY = 'productCardDetailFont';
export const POS_CARD_FONT_MIN = 6;
export const POS_CARD_FONT_MAX = 32;

// ---- Per-client branding and payments -------------------------------------

/**
 * Where a client's own payment methods live, under posTenants/<clientId>/.
 *
 * In the CLIENT's subtree, not posClients, because this is the club deciding
 * how it takes money — not the deployment deciding for it. Same reasoning as
 * the theme: a club changing its own settings should not need an
 * administrator.
 */
export const POS_PAYMENT_METHODS_NODE = 'paymentMethods';

/** A payment label is a word on a button; it has to fit on one. */
export const POS_PAYMENT_LABEL_MAX_LENGTH = 30;

/** More than this and the confirm step becomes a menu rather than a choice. */
export const POS_PAYMENT_METHODS_MAX = 8;

/**
 * The club's logo and font, under posTenants/<clientId>/.
 *
 * ITS OWN NODE rather than a key in `config`, for two reasons. The logo is a
 * base64 image of up to ~60KB and `config` is subscribed to by every screen,
 * so folding it in would re-download the logo on every colour change. And
 * `config` validates every value as a hex colour in the rules, which a data
 * URL is not.
 */
export const POS_BRANDING_NODE = 'branding';

/**
 * The typefaces a client can choose between.
 *
 * EVERY ONE OF THESE IS ALREADY ON THE DEVICE. Nothing here is downloaded, so
 * a choice takes effect instantly, costs no bandwidth, and cannot fail to load
 * and silently fall back to something else — which is what happens today with
 * 'Inter' and 'Archivo', named all over the stylesheets and never actually
 * fetched by index.html.
 *
 * THE KEY IS STORED, NOT THE STACK. A stack can then be corrected — a better
 * fallback, a newly common face — without touching a single client's data.
 */
export const POS_FONT_CHOICES = [
  {
    key: 'system',
    label: 'System default',
    hint: 'Matches the device — what this till uses today',
    stack: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  },
  {
    key: 'humanist',
    label: 'Humanist sans',
    hint: 'Open and friendly, a shade wider',
    stack: "'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  {
    key: 'grotesque',
    label: 'Neutral sans',
    hint: 'Plain and even — the least decorated choice',
    stack: "Arial, 'Helvetica Neue', Helvetica, sans-serif",
  },
  {
    key: 'wide',
    label: 'Wide sans',
    hint: 'Roomier letters, easiest to read at a distance',
    stack: "Verdana, Geneva, 'DejaVu Sans', sans-serif",
  },
  {
    key: 'rounded',
    label: 'Rounded sans',
    hint: 'Softer shapes, a little informal',
    stack: "'Trebuchet MS', 'Lucida Grande', 'Lucida Sans Unicode', sans-serif",
  },
  {
    key: 'serif',
    label: 'Serif',
    hint: 'Traditional, with the small strokes on each letter',
    stack: "Georgia, 'Times New Roman', Times, serif",
  },
];

/** Just the keys, for validating a stored choice. */
export const POS_FONT_KEYS = POS_FONT_CHOICES.map((font) => font.key);

/** What a client that has never chosen gets — today's typeface, unchanged. */
export const POS_FONT_DEFAULT = 'system';

/**
 * Fonts a club uploads itself (.ttf, .otf, .woff, .woff2), under
 * posTenants/<clientId>/.
 *
 * TWO NODES: the list of names every Store Details screen reads, and the file
 * data itself, which only the till that USES the font downloads — a font is
 * hundreds of kilobytes and must not ride along with a list of names.
 */
export const POS_CUSTOM_FONTS_NODE = 'customFonts';
export const POS_CUSTOM_FONT_DATA_NODE = 'customFontData';

/**
 * A stored font choice pointing at an uploaded font is this prefix plus the
 * font's id — e.g. "custom-OaB3…". Kept short so the key still fits the 32
 * characters branding/fontFamily allows (7 + a 20-char push id).
 */
export const POS_CUSTOM_FONT_PREFIX = 'custom-';

/** The CSS family name an uploaded font is registered under is this + its id. */
export const POS_CUSTOM_FONT_FAMILY_PREFIX = 'PosFont_';

/**
 * The font file types a club can import, one entry per extension.
 *
 *   mime       - what the stored data URL is normalised to start with, since
 *                browsers report font files inconsistently (or not at all)
 *   format     - the CSS @font-face / FontFace format() hint
 *   signatures - the file's first four bytes, read as a big-endian number, so
 *                a renamed file (a .png called .ttf) is refused before upload
 *   label      - how the type is named to the user
 */
export const POS_CUSTOM_FONT_TYPES = [
  { ext: '.ttf', mime: 'font/ttf', format: 'truetype', signatures: [0x00010000, 0x74727565], label: 'TrueType' },
  { ext: '.otf', mime: 'font/otf', format: 'opentype', signatures: [0x4f54544f, 0x00010000], label: 'OpenType' },
  { ext: '.woff', mime: 'font/woff', format: 'woff', signatures: [0x774f4646], label: 'WOFF' },
  { ext: '.woff2', mime: 'font/woff2', format: 'woff2', signatures: [0x774f4632], label: 'WOFF2' },
];

/** The accepted extensions, for the file picker's `accept` and messages. */
export const POS_CUSTOM_FONT_EXTENSIONS = POS_CUSTOM_FONT_TYPES.map((type) => type.ext);

/**
 * The largest font file accepted, in bytes. Base64 adds a third, so this stays
 * under the 2,100,000-character cap in database.rules.json.
 */
export const POS_CUSTOM_FONT_MAX_BYTES = 1.5 * 1024 * 1024;

/** A font's display name must fit in the dropdown. */
export const POS_CUSTOM_FONT_NAME_MAX_LENGTH = 40;

/** How many fonts one club may keep uploaded. */
export const POS_CUSTOM_FONTS_MAX = 10;

// ---- Active and reserve stock ---------------------------------------------
//
// TWO STOCKS FOR ONE PRODUCT. `supplies` is the ACTIVE stock — what is on the
// shelf or in the fridge, and the only one a sale can draw on. `reserveUnits`
// is the back stock: unopened boxes in the storeroom, which sell nothing until
// somebody carries a box out and refills the fridge.
//
// RESERVE IS STORED AS ONE NUMBER OF UNITS, never as {boxes, loose}. Boxes and
// loose pieces are a way of READING a single quantity, not two quantities: at
// 24 to a box, "3 boxes and 30 loose" and "4 boxes and 6 loose" are the same
// 102 units, and storing both halves is how the two come to disagree. The
// split is derived for display by posSplitPacks, and the arithmetic — a
// replenishment moving units from one to the other — stays one addition and
// one subtraction.

/** Units in one unopened pack. 0 or absent means this product has no packs. */
export const POS_PACK_SIZE_KEY = 'unitsPerPack';

/** The most units a single pack may hold. A pallet is not a box. */
export const POS_PACK_SIZE_MAX = 10000;

/** The most units that can be recorded into one product's reserve at once. */
export const POS_RESERVE_UNITS_MAX = 1000000;

/**
 * What one piece is called, when a product has not said.
 *
 * GENERIC ON PURPOSE. This till sells drinks, snacks, grips and shirts, so the
 * default noun cannot be "bottle" — a club that wants "bottle" sets it on the
 * product, and everything else reads correctly without anyone touching it.
 */
export const POS_UNIT_LABEL_DEFAULT = 'piece';

/** What one unopened pack is called, when a product has not said. */
export const POS_PACK_LABEL_DEFAULT = 'box';

/** A noun has to fit on a chip beside a number. */
export const POS_STOCK_LABEL_MAX_LENGTH = 24;

/**
 * The two stocks, as Inventory's top-level tabs.
 *
 * Active first because it is the one a counter looks at all day; Reserve is
 * visited when a delivery arrives or the fridge runs down.
 */
export const POS_STOCK_SCOPES = [
  { key: 'active', label: 'Active Stocks', description: 'On the shelf — what sales draw on' },
  { key: 'reserve', label: 'Reserve Stocks', description: 'Unopened packs in the storeroom' },
];

/** Just the keys, for validating a restored choice. */
export const POS_STOCK_SCOPE_KEYS = POS_STOCK_SCOPES.map((scope) => scope.key);

/**
 * Whether this client keeps a reserve (storeroom) stock at all, under
 * posTenants/<id>/config. Absent means ON — every client before this setting
 * existed ran with a reserve. Off, the till keeps one standalone active count:
 * products are registered and restocked straight on the Active tab.
 */
export const POS_RESERVE_ENABLED_KEY = 'reserveEnabled';

/** What Inventory opens on — the stock the till actually sells from. */
export const POS_STOCK_SCOPE_DEFAULT = 'active';

// ---- Inventory views ------------------------------------------------------

/**
 * How Inventory lists its products.
 *
 * `card` is the picture-led view the screen has always had — the one to use
 * when you are looking for a product you would recognise by its packaging.
 * `grid` is the same products as rows of text, which is what you want when you
 * are checking a shelf against a delivery note: every figure lined up in a
 * column, and the stock count editable in place.
 */
export const POS_INVENTORY_VIEWS = [
  { key: 'grid', label: 'Grid', description: 'Rows of text, stock editable in place' },
  { key: 'card', label: 'Card', description: 'Picture-led cards' },
];

/** Just the keys, for validating a restored choice. */
export const POS_INVENTORY_VIEW_KEYS = POS_INVENTORY_VIEWS.map((view) => view.key);

/**
 * What Inventory opens on. The card view, because that is what the screen
 * already did — a new view should not change what anyone sees until they ask
 * for it.
 */
export const POS_INVENTORY_VIEW_DEFAULT = 'card';

// ---- Sorting --------------------------------------------------------------

/** A-Z, cheapest first, fewest sold, emptiest shelf. */
export const POS_SORT_ASC = 'asc';
/** Z-A, dearest first, best sellers, fullest shelf. */
export const POS_SORT_DESC = 'desc';

/**
 * The one sort key the till cannot answer from the product list alone — it
 * needs the orders. Named so the screen can test for it without a bare string
 * in a condition that decides whether to open a database listener.
 */
export const POS_SORT_BY_SOLD = 'sold';

/**
 * The hand-arranged order. The only sort key that is not computed from the
 * products: it is a saved list of ids (see POS_CUSTOM_SORT_NODE), so the
 * direction button means nothing on it and is hidden.
 */
export const POS_SORT_BY_CUSTOM = 'custom';

/**
 * How the product grid can be ordered.
 *
 * `defaultDirection` is applied when the sort FIELD changes, because the useful
 * end of a list depends on what is being sorted: picking "Items sold" almost
 * always means "show me the best sellers", while picking "Stocks available"
 * almost always means "show me what is running out". Starting both at A-Z's
 * ascending would make every such choice a two-click operation. Once the
 * direction button is used it stays put until the field changes again.
 *
 * `ascLabel` / `descLabel` name the two ends in the words of THAT field — the
 * direction button says "Cheapest first", not "Ascending", because a till is
 * read at a glance by someone who is not thinking about sort order.
 */
export const POS_PRODUCT_SORTS = [
  {
    key: 'name',
    label: 'Name',
    defaultDirection: POS_SORT_ASC,
    ascLabel: 'A to Z',
    descLabel: 'Z to A',
  },
  {
    key: 'category',
    label: 'Category',
    defaultDirection: POS_SORT_ASC,
    ascLabel: 'A to Z',
    descLabel: 'Z to A',
  },
  {
    key: 'price',
    label: 'Price',
    defaultDirection: POS_SORT_ASC,
    ascLabel: 'Cheapest first',
    descLabel: 'Dearest first',
  },
  {
    key: POS_SORT_BY_SOLD,
    label: 'Items sold',
    defaultDirection: POS_SORT_DESC,
    ascLabel: 'Fewest sold',
    descLabel: 'Best sellers',
  },
  {
    key: 'stock',
    label: 'Stocks available',
    defaultDirection: POS_SORT_ASC,
    ascLabel: 'Lowest stock',
    descLabel: 'Highest stock',
  },
  {
    key: POS_SORT_BY_CUSTOM,
    label: 'Custom',
    defaultDirection: POS_SORT_ASC,
    ascLabel: 'Custom order',
    descLabel: 'Custom order',
  },
];

/**
 * Where each screen's hand-arranged order is stored, under
 * posTenants/<clientId>/. One array of product ids per list key below.
 */
export const POS_CUSTOM_SORT_NODE = 'customSort';

/**
 * The custom order lists. There is ONE, shared by the Products screen and
 * Inventory: arranging and saving on either screen reorders both, so staff
 * find a product in the same place wherever they look. Stored under the
 * "products" key, which is where the Products screen's order already lived.
 * Keys are Firebase keys: no dots.
 */
export const POS_CUSTOM_SORT_LISTS = {
  SHARED: 'products',
};

/**
 * Where each screen's last chosen sort is stored, under
 * posTenants/<clientId>/. One `{ key, direction, updatedAt }` record per
 * screen below, so a client's tills open in the order last picked instead of
 * always starting on Name.
 */
export const POS_SORT_PREFERENCE_NODE = 'sortPreference';

/**
 * The screens that remember their sort. Kept apart on purpose: the till and
 * Inventory are used for different jobs (selling vs. checking stock), so
 * picking "Stocks available" on Inventory should not reorder the till.
 * Keys are Firebase keys: no dots.
 */
export const POS_SORT_PREFERENCE_SCREENS = {
  PRODUCTS: 'products',
  INVENTORY: 'inventory',
};

/** How far (px) a mouse must move before a press becomes a drag. */
export const POS_DRAG_THRESHOLD_PX = 6;

/** How long (ms) a finger must rest on a card before it can be dragged. */
export const POS_DRAG_LONG_PRESS_MS = 280;

/** Just the keys, for validating a stored or restored choice. */
export const POS_PRODUCT_SORT_KEYS = POS_PRODUCT_SORTS.map((sort) => sort.key);

/**
 * What the grid opens on: the order it has always been in, so the screen looks
 * the same to anyone who never touches the control.
 */
export const POS_PRODUCT_SORT_DEFAULT = 'name';

// ---- VAT ------------------------------------------------------------------
//
// SET PER CLIENT, from /admin -> Client Registration -> VAT. It lives on
// the client PROFILE rather than in posGlobalConfig because one deployment can
// run a VAT-registered club beside one that is not, and beside one in another
// country at another rate — a single global figure would be wrong for two of
// the three. The profile is also admin-only to write, which is what the
// instruction "the config ... should be in /admin" actually asks for: a club's
// own staff can repaint their till, but they cannot change what tax it charges.

/** No VAT at all. The totals block looks exactly as it did before VAT existed. */
export const POS_VAT_MODE_NONE = 'none';

/**
 * The price on the shelf ALREADY contains the tax. The customer pays the
 * subtotal; the breakdown only says how much of it was tax.
 */
export const POS_VAT_MODE_INCLUSIVE = 'inclusive';

/**
 * The price on the shelf is before tax. VAT is added, so the customer pays
 * MORE than the subtotal.
 */
export const POS_VAT_MODE_EXCLUSIVE = 'exclusive';

/**
 * The three modes, in the order they are offered. The description is what the
 * admin screen prints under each choice — "inclusive" and "exclusive" are one
 * letter apart and mean opposite things to the customer's wallet, so neither
 * is offered as a bare label.
 */
export const POS_VAT_MODES = [
  {
    key: POS_VAT_MODE_NONE,
    label: 'No VAT',
    description: 'No tax line anywhere. The customer pays the subtotal.',
  },
  {
    key: POS_VAT_MODE_INCLUSIVE,
    label: 'VAT Inclusive',
    description: 'VAT is already inside the shelf price. The total does not change — the receipt just shows how much of it was tax.',
  },
  {
    key: POS_VAT_MODE_EXCLUSIVE,
    label: 'VAT Exclusive',
    description: 'VAT is added on top of the shelf price, so the customer pays more than the subtotal.',
  },
];

/** Just the keys, for validating what comes back from the database. */
export const POS_VAT_MODE_KEYS = POS_VAT_MODES.map((mode) => mode.key);

/** What the rate field opens at. 12% is the Philippine rate. */
export const POS_VAT_RATE_DEFAULT = 12;

/**
 * The most VAT can be set to. 100 would make an inclusive sale entirely tax
 * and its item cost zero, which is the point past which the arithmetic stops
 * describing anything real.
 */
export const POS_VAT_RATE_MAX = 99.99;

/**
 * HOW AN INCLUSIVE TOTAL IS SPLIT. Two methods, and they do not agree.
 *
 *   'proportional' — VAT is rate% OF THE TOTAL.
 *                    ₱140 at 12%  ->  VAT ₱16.80, item cost ₱123.20
 *
 *   'divisor'      — VAT is what was ADDED to reach the total, i.e.
 *                    total - total / (1 + rate/100).
 *                    ₱140 at 12%  ->  VAT ₱15.00, item cost ₱125.00
 *
 * 'proportional' is set here because it is the breakdown that was specified,
 * figure for figure. BE AWARE THAT 'divisor' IS THE ONE TAX AUTHORITIES USE,
 * the BIR included: a VAT-inclusive receipt in the Philippines is required to
 * show the VATable sale as total / 1.12. If these receipts are ever given to a
 * customer as proof of tax paid, change this one constant to 'divisor' — every
 * screen, receipt and stored order follows it, because posVatBreakdown is the
 * only place the split is worked out.
 *
 * Orders already recorded keep the figures they were charged at; see the vat
 * fields written by placePosOrder.
 */
export const POS_VAT_INCLUSIVE_METHOD = 'proportional';

// ---- Theme ----
//
// The five colours a client can set for its own till, and the CSS custom
// property each one drives. ConfigContext reads these keys off config and
// injects them over css/global.css's :root — see the effect there.
//
// Named to match the court-booking system's own theme config, field for
// field, so a club that has both recognises the screen.
export const POS_THEME_FIELDS = [
  {
    key: 'themeTextColor',
    label: 'Text Color',
    hint: 'Main body & heading text (usually dark)',
  },
  {
    key: 'themeBackgroundColor',
    label: 'Screen Background',
    hint: 'Page background behind all content',
  },
  {
    key: 'themeHeaderBackgroundColor',
    label: 'Header Background',
    hint: 'Side panel and top bar background',
  },
  {
    key: 'themeActiveColor',
    label: 'Active Button Color',
    hint: 'Primary buttons, active tabs/steps, focus outlines',
  },
  {
    key: 'themeBrandColor',
    label: 'Court Name / Logo Color',
    hint: 'Court name text in the header and logo areas',
  },
  {
    key: 'themeActiveTextColor',
    label: 'Active Button Text Color',
    hint: 'Text on primary buttons and active menu items',
    // The only field that may be left blank. Empty means "work it out" —
    // black or white, whichever actually contrasts with the Active Button
    // Color — which is right far more often than a colour picked by hand and
    // then forgotten about when the button colour changes.
    optional: true,
  },
];

/** Just the keys, for validating what comes back from the database. */
export const POS_THEME_KEYS = POS_THEME_FIELDS.map((field) => field.key);

// ---- Category icons ----
/**
 * The offline half of the icon picker. Chosen for a counter: food, drink and
 * the handful of non-consumables a club actually sells, rather than a full
 * emoji keyboard nobody wants to scroll behind a till.
 */
export const POS_CATEGORY_ICON_EMOJIS = [
  '🥤', '☕', '🧃', '💧', '🍺', '🍿', '🍫', '🍬',
  '🍪', '🥨', '🍞', '🍔', '🌭', '🍕', '🍜', '🍱',
  '🍎', '🍌', '🧊', '🧴', '💊', '🩹', '🧢', '👕',
  '🎾', '🏓', '🏸', '⚽', '🎒', '🔋', '🧻', '📦',
];

/**
 * Iconify's public API — free, no key, CORS-enabled, ~200k open-source icons.
 * Chosen over the Noun Project and Flaticon, both of which need a paid key
 * and a server-side proxy to keep it secret.
 */
export const POS_ICONIFY_SEARCH_URL = 'https://api.iconify.design/search';
export const POS_ICONIFY_BASE_URL = 'https://api.iconify.design';
/** Results per search. Enough to choose from, few enough to scan. */
export const POS_ICONIFY_SEARCH_LIMIT = 48;

/**
 * Longest icon value accepted. An emoji is a few bytes and the longest real
 * Iconify id is well under this — the cap is only here so a malformed paste
 * cannot write something unbounded into every till's category node.
 */
export const POS_CATEGORY_ICON_MAX_LEN = 64;

// ---- Product customization options ----
//
// Extra questions a product asks when it is sold — a milk tea's sugar level,
// its add-ons, a name to write on the cup. Stored on the product itself as
// `customizations`, an array of option records, because the till reads the
// whole product list anyway and an option never makes sense without the
// product it belongs to.
//
// Each option is answered one of two ways:
//   choice - picked from the club's own list (Sugar: 0% / 50% / 100%), each
//            choice optionally adding to the price (Pearl +₱10)
//   text   - typed at the counter (Name on cup)

/** The product field holding its customization options. */
export const POS_CUSTOMIZATIONS_KEY = 'customizations';

/** How an option is answered. */
export const POS_CUSTOMIZATION_TYPES = {
  CHOICE: 'choice',
  TEXT: 'text',
};

/** The two answer types, as the option form offers them. */
export const POS_CUSTOMIZATION_TYPE_CHOICES = [
  { key: POS_CUSTOMIZATION_TYPES.CHOICE, label: 'Selection', description: 'Staff pick from a list you set' },
  { key: POS_CUSTOMIZATION_TYPES.TEXT, label: 'Custom input', description: 'Staff type the answer at the counter' },
];

/** The most options one product may ask. More than this is a menu, not a product. */
export const POS_CUSTOMIZATIONS_MAX = 12;

/** The most choices one option may list. */
export const POS_CUSTOMIZATION_CHOICES_MAX = 30;

/** An option's label, and a choice's name, have to fit on a receipt line. */
export const POS_CUSTOMIZATION_LABEL_MAX_LENGTH = 40;

/** The longest answer staff may type into a custom-input option. */
export const POS_CUSTOMIZATION_TEXT_MAX_LENGTH = 80;

/** The most one choice may add to the price of one item. */
export const POS_CUSTOMIZATION_PRICE_MAX = 100000;

// ---- Product extras ----
//
// Paid add-ons counted per item — "extra pearl", "extra shot" — set up beside
// the customization options and stored on the product as `extras`. Unlike an
// option, an extra is not a question with answers: staff say HOW MANY of it
// go into each item (0 by default), and each one adds its price.

/** The product field holding its extras. */
export const POS_EXTRAS_KEY = 'extras';

/** The most extras one product may offer. */
export const POS_EXTRAS_MAX = 20;

/** An extra's optional description — a hint for staff, not a paragraph. */
export const POS_EXTRA_DESCRIPTION_MAX_LENGTH = 120;

/** The most of one extra a single item may take (6 extra pearls is a lot). */
export const POS_EXTRA_QTY_MAX = 10;

/**
 * How a chosen extra is labelled on the order line, the receipt and in Sales:
 * "Extra: Pearl × 2". One fixed word, so every extra reads the same way.
 */
export const POS_EXTRA_SELECTION_LABEL = 'Extra';

// ---- Cost price ----

/**
 * The product field holding what ONE costs the club to make or buy — the
 * "puhunan". Optional: absent means "not entered", never zero, so a product
 * nobody has costed is not reported as pure profit.
 */
export const POS_COST_PRICE_KEY = 'costPrice';
